import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { ListFilters } from "../../../../../interfaces/customer.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  status: any[];
  cities: any[];
  allCities: any[];
  allStatus: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  status, cities, allCities, allStatus, dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  // Calculate initial filtersApplied state
  const isApplied = filters.searchText !== '' ||
    filters.city !== '' ||
    filters.status !== '1';

  const [filtersApplied, setFiltersApplied] = useState(false);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle Real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied = filtersToApply.searchText !== '' ||
      filtersToApply.city !== '' ||
      filtersToApply.status !== '1';

    setFiltersApplied(applied);

    onApply(filtersToApply);
  }, [debouncedSearchText, filters.city, filters.status, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      city: '',
      status: '1'
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-8 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("customers.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.city && filters.city.toString().trim() ? [filters.city] : []}
                options={(cities || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allCities || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.city}
                name={VALIDATION_FIELD_MAPPING.city}
                searchText={dropdownSearchData.city}
                onSearch={onDropdownSearchChange}
                placeholder={t("customers.list.pageTexts.filters.selectCity")}
                label={t("customers.list.pageTexts.filters.city")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-6">
              <Dropdown
                values={filters.status && filters.status.toString().trim() ? [filters.status] : []}
                options={(status || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                allOptions={(allStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id,
                }))}
                id={VALIDATION_FIELD_MAPPING.status}
                name={VALIDATION_FIELD_MAPPING.status}
                searchText={dropdownSearchData.status}
                onSearch={onDropdownSearchChange}
                placeholder={t("customers.list.pageTexts.filters.selectStatus")}
                label={t("customers.list.pageTexts.filters.status")}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filter;